import { clsx } from 'clsx'
import type { InfraHostMetrics, InfraThreshold } from '@/lib/types'

function metricVariant(
  value: number,
  metric: string,
  thresholds: InfraThreshold[],
): 'normal' | 'warning' | 'critical' {
  const t = thresholds.find((th) => th.metric === metric && th.enabled)
  if (!t) return 'normal'
  if (value >= t.critical) return 'critical'
  if (value >= t.warning) return 'warning'
  return 'normal'
}

const VARIANT_BORDER = {
  normal: 'border-surface-3',
  warning: 'border-warning',
  critical: 'border-danger',
} as const

interface SmtpBoxGridProps {
  hosts: InfraHostMetrics[]
  thresholds: InfraThreshold[]
  onSelect: (hostname: string) => void
}

export function SmtpBoxGrid({ hosts, thresholds, onSelect }: SmtpBoxGridProps): React.JSX.Element {
  if (hosts.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-surface-3 p-6 text-center text-sm text-text-tertiary">
        No SMTP boxes reporting metrics yet
      </p>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {hosts.map((host) => {
        const cpuV = metricVariant(host.cpu_percent, 'cpu', thresholds)
        const memV = metricVariant(host.memory_percent, 'memory', thresholds)
        const worst =
          cpuV === 'critical' || memV === 'critical'
            ? 'critical'
            : cpuV === 'warning' || memV === 'warning'
              ? 'warning'
              : 'normal'

        return (
          <button
            key={host.hostname}
            type="button"
            onClick={() => onSelect(host.hostname)}
            className={clsx(
              'rounded-xl border bg-surface-0 p-4 text-left transition-colors hover:bg-surface-1',
              VARIANT_BORDER[worst],
            )}
          >
            <p className="mb-2 text-sm font-semibold text-text-primary">{host.hostname}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-text-tertiary">CPU</span>
              <span
                className={clsx(
                  'text-right font-medium',
                  cpuV === 'critical'
                    ? 'text-danger'
                    : cpuV === 'warning'
                      ? 'text-warning'
                      : 'text-text-primary',
                )}
              >
                {host.cpu_percent.toFixed(1)}%
              </span>
              <span className="text-text-tertiary">Mem</span>
              <span
                className={clsx(
                  'text-right font-medium',
                  memV === 'critical'
                    ? 'text-danger'
                    : memV === 'warning'
                      ? 'text-warning'
                      : 'text-text-primary',
                )}
              >
                {host.memory_percent.toFixed(1)}%
              </span>
              <span className="text-text-tertiary">Disk</span>
              <span className="text-right font-medium text-text-primary">
                {host.disk_percent.toFixed(1)}%
              </span>
              <span className="text-text-tertiary">Load</span>
              <span className="text-right font-medium text-text-primary">
                {host.load_1.toFixed(2)}
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
